// Blast-radius interception. Any enemy missile or flying object inside an ACTIVE
// explosion (radius>0) is destroyed, emits a KillEvent, and spawns a new radius-0
// explosion at the kill point. New blasts start at radius 0 so they can't clear
// the screen this tick; the chain propagates over subsequent ticks as they grow.

import type { EnemyMissile, Explosion, FlyingObject, KillEvent } from '../state/types';
import type { ResolveCollisions } from './contracts';
import { containsPoint } from '../geometry/circle';
import { createExplosion } from './explosions';

export const resolveCollisions: ResolveCollisions = (state) => {
  const active = state.explosions.filter((e) => e.radius > 0);
  if (active.length === 0) return state;

  let nextId = state.nextEntityId;
  const explosions: Explosion[] = [...state.explosions];
  const kills: KillEvent[] = [...state.pendingKills];

  const inBlast = (p: { x: number; y: number }): boolean =>
    active.some((ex) => containsPoint(ex.pos, ex.radius, p));

  const survivingEnemies: EnemyMissile[] = [];
  for (const e of state.enemyMissiles) {
    if (inBlast(e.pos)) {
      explosions.push(createExplosion(nextId++, e.pos, 'player'));
      kills.push({ enemyId: e.id, kind: e.kind, at: e.pos });
    } else {
      survivingEnemies.push(e);
    }
  }

  const survivingFlyers: FlyingObject[] = [];
  for (const f of state.flyingObjects) {
    if (inBlast(f.pos)) {
      explosions.push(createExplosion(nextId++, f.pos, 'player'));
      kills.push({ enemyId: f.id, kind: f.kind, at: f.pos });
    } else {
      survivingFlyers.push(f);
    }
  }

  return {
    ...state,
    enemyMissiles: survivingEnemies,
    flyingObjects: survivingFlyers,
    explosions,
    pendingKills: kills,
    nextEntityId: nextId,
  };
};
